import type { BiomassDataRow } from './productionUnitTable'
import { resolveCanonicalLotKey } from './lotIdentity'
import { toDayKeyDateOnly } from './productionFeedKgDerivation'

// v2.1 finding #36: rows created before finding #12 landed (2026-06-19) can
// have duplicate (lot, day) entries. The Database feed-autofill derivation
// in productionFeedKgDerivation.ts skips groups whose count !== 1, so those
// legacy duplicates suppress feed autofill ("Actualizar registros" cannot
// cure them either). The cure is a one-shot operator-confirmed migration
// that fuses the duplicates into a single row. This helper enumerates which
// groups exist so the migration modal can preview them.

export type LegacyDuplicateGroup = {
  dayKey: string // YYYY-MM-DD (date-only; browser-local via toDayKeyDateOnly,
                 // or farm-local when `options.timeZone` is provided — see #47).
  lotKey: string // canonical lot token from resolveCanonicalLotKey
  /** Display label for the lot — prefers `lot` (user-friendly), falls back to lotKey. */
  lotLabel: string
  rows: BiomassDataRow[]
}

const resolveDuplicateLotToken = (row: BiomassDataRow): string => {
  const token = resolveCanonicalLotKey(row.lotId, row.lot)
  return token === 'unknown-lot' ? '' : token
}

const isArchived = (row: BiomassDataRow): boolean => row?.archived === '1'

/**
 * Enumerate groups of rows that share the same `(calendar day, canonical
 * lot)` pair. A group with 2+ rows is a legacy duplicate that should be
 * fused. Archived rows are excluded — they're already retired and operators
 * cannot edit them.
 *
 * v2.1 #47: when `options.timeZone` is provided, the day key is derived in
 * that zone (typically the farm timezone, falling back to browser). Without
 * it, the legacy UTC-prefix behavior is preserved — but late-evening rows
 * whose stored ISO-Z crossed the UTC midnight at parse time will then
 * group with the *next* calendar day, which is the exact root cause of #47.
 * Callers from Datatable should always pass the farm timezone.
 *
 * NOTE: This is the discovery half of the migration. The merge logic itself
 * is intentionally separate so the modal can preview without touching state.
 */
export const buildLegacyDuplicateGroups = (
  rows: BiomassDataRow[],
  options?: { timeZone?: string }
): LegacyDuplicateGroup[] => {
  if (!Array.isArray(rows) || rows.length === 0) return []

  const dayKeyOptions = options?.timeZone ? { timeZone: options.timeZone } : undefined
  const buckets = new Map<string, LegacyDuplicateGroup>()
  for (const row of rows) {
    if (!row || isArchived(row)) continue
    const lotKey = resolveDuplicateLotToken(row)
    const dayKey = toDayKeyDateOnly(row.date ?? '', dayKeyOptions)
    if (!lotKey || !dayKey) continue

    const key = `${dayKey}::${lotKey}`
    const existing = buckets.get(key)
    if (existing) {
      existing.rows.push(row)
    } else {
      buckets.set(key, {
        dayKey,
        lotKey,
        lotLabel: String(row.lot ?? lotKey),
        rows: [row]
      })
    }
  }

  const groups: LegacyDuplicateGroup[] = []
  for (const group of buckets.values()) {
    if (group.rows.length >= 2) groups.push(group)
  }

  // Stable order: oldest day first, then lotLabel asc.
  groups.sort((a, b) => {
    if (a.dayKey !== b.dayKey) return a.dayKey < b.dayKey ? -1 : 1
    return a.lotLabel < b.lotLabel ? -1 : 1
  })
  return groups
}

/**
 * Aggregate totals for the confirmation modal header.
 */
export const summarizeLegacyDuplicateGroups = (groups: LegacyDuplicateGroup[]): {
  groupCount: number
  affectedRowCount: number
  rowsToDelete: number
} => {
  let affectedRowCount = 0
  let rowsToDelete = 0
  for (const group of groups) {
    affectedRowCount += group.rows.length
    rowsToDelete += Math.max(0, group.rows.length - 1)
  }
  return {
    groupCount: groups.length,
    affectedRowCount,
    rowsToDelete
  }
}
